import React, { useEffect, useRef, useState } from "react";
import PdfReader from "./reading/PdfReader";
import ReadingUpsell from "./reading/ReadingUpsell";
import { fetchReadingConfig } from "../api";
import { track } from "../utils/analytics";
import IrabToolCta from "./IrabToolCta";

/**
 * PublicReaderPage — the anonymous-reachable PDF reader at /reader.
 *
 * Mounted from index.js BEFORE App, so none of the app's auth gates or URL
 * param handling ever run on this route (same contract as /irab, /sarf and
 * /typing). The visitor's PDF never uploads: pdf.js renders it locally and
 * only rasterized page images go to the OCR endpoint. Free tier limits are
 * enforced server-side; a visitor with a stored token gets their real tier
 * automatically (api.js attaches the token).
 *
 * English UI, like /typing.
 */
export default function PublicReaderPage() {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [settings, setSettings] = useState({});
  const [file, setFile] = useState(null); // local File — never uploaded
  const fileInputRef = useRef(null);
  const hasToken = !!localStorage.getItem("token");

  useEffect(() => {
    document.title = "Read Arabic PDFs with Iʿrāb — Murājaʿa";
    fetchReadingConfig()
      .then((cfg) => {
        setEnabled(!!cfg.enabled);
        setSettings({
          // Public mode has no server library — resume/remember is meaningless.
          rememberPage: false,
          ocrHighRes: cfg.ocr_high_res !== false,
          translationMaxChars: cfg.translation_max_chars ?? 300,
          translationRemaining: cfg.translations_remaining_today ?? null,
        });
      })
      .catch(() => setEnabled(true)); // fail open to the reader; server enforces
  }, []);

  const pickFile = (f) => {
    if (!f) return;
    track("reader-file-open", { context: "public" });
    setFile(f);
  };

  return (
    <div className="dark-scope min-h-screen flex flex-col" style={{ background: "var(--ink)" }}>
      {/* Header */}
      <header className="flex items-center justify-between px-5 py-4 max-w-4xl w-full mx-auto">
        <a href="https://muraja.app" className="flex items-baseline gap-2 no-underline">
          <span className="font-amiri text-2xl text-dt-gold-bright">مراجعة</span>
          <span className="text-xs text-dt-parchment-dim tracking-wide">murājaʿa</span>
        </a>
        <a
          href="/"
          onClick={() => track("reader-signup-click", { context: "public", kind: "header" })}
          className="text-sm text-dt-parchment-dim hover:text-dt-gold-bright transition-colors"
        >
          {hasToken ? "Open the app →" : "Sign in →"}
        </a>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-start justify-center px-3 sm:px-4 pb-8">
        {enabled === false ? (
          <ComingSoon />
        ) : file ? (
          <div className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-4xl h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={() => setFile(null)}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
                  aria-label="Choose another file"
                  title="Choose another file"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2 className="text-lg font-semibold text-ink-800 truncate">
                  {file.name.replace(/\.pdf$/i, "")}
                </h2>
              </div>
              <p className="text-xs text-ink-400 shrink-0 hidden sm:block">
                Your file stays in your browser
              </p>
            </div>
            <PdfReader
              context="public"
              pdf={{ file, title: file.name.replace(/\.pdf$/i, "") }}
              settings={settings}
            />
          </div>
        ) : (
          <FilePicker onPick={pickFile} inputRef={fileInputRef} />
        )}
      </main>

      {/* Only alongside a working tool — pitching another tool next to a
          "coming soon" notice would be incoherent. */}
      {enabled !== false && <IrabToolCta context="reader-public" />}

      {/* Footer */}
      <footer className="px-5 py-4 text-center text-xs text-dt-parchment-dim space-x-3">
        <a href="https://muraja.app" className="hover:text-dt-parchment">muraja.app</a>
        <a href="https://muraja.app/tools/pdf-irab/" className="hover:text-dt-parchment">About this tool</a>
        <a href="https://muraja.app/privacy.html" className="hover:text-dt-parchment">Privacy</a>
        <a href="https://muraja.app/terms.html" className="hover:text-dt-parchment">Terms</a>
        <a href="https://muraja.app/ai-policy.html" className="hover:text-dt-parchment">AI Policy</a>
      </footer>
    </div>
  );
}

function FilePicker({ onPick, inputRef }) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div className="w-full max-w-2xl flex flex-col gap-4 mt-4">
      <div className="text-center">
        <h1 className="font-cormorant font-semibold text-3xl" style={{ color: "var(--gold-bright)" }}>
          Read Arabic PDFs with Iʿrāb
        </h1>
        <p className="text-sm text-dt-parchment-dim mt-2 max-w-lg mx-auto">
          Open any Arabic book or scanned page — tap words for their meaning, color a
          sentence's grammar word by word, or translate it whole. Free, no account needed.
        </p>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f && /\.pdf$/i.test(f.name)) onPick(f);
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
        className={`surface-glass rounded-2xl shadow-2xl px-6 py-12 text-center cursor-pointer transition-[filter] hover:brightness-110 ${
          dragOver ? "ring-2 ring-dt-gold" : ""
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            onPick(f);
          }}
        />
        <div className="text-4xl mb-3" aria-hidden="true">📖</div>
        <p className="font-cormorant font-semibold text-xl" style={{ color: "var(--gold-bright)" }}>
          Choose a PDF — or drop it here
        </p>
        <p className="text-xs text-dt-parchment-dim mt-2">
          Your PDF never leaves your browser — pages are rendered locally. Only a page
          image is sent when you ask for text detection, and only selected sentences
          when you ask for a translation.
        </p>
      </div>

      <ReadingUpsell kind="banner" context="public" />
    </div>
  );
}

function ComingSoon() {
  return (
    <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-lg px-6 py-10 text-center">
      <p className="font-cormorant font-semibold text-2xl text-dt-parchment mb-2">
        The PDF reader is almost ready
      </p>
      <p className="text-sm text-dt-parchment-dim mb-6">
        Read any Arabic PDF with tap-for-iʿrāb, word meanings, and sentence translation.
        Start your free trial and be first to use it.
      </p>
      <a
        href="/?signup=1"
        onClick={() => track("reader-signup-click", { context: "public", kind: "coming-soon" })}
        className="inline-block px-5 py-2.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter] no-underline"
      >
        Start your 7-day free trial
      </a>
    </div>
  );
}
